import { useEffect, useState } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

interface Location { id: number; name: string; }
interface Inventory { id: number; name: string; stock: number; jenis: 'bahan_baku' | 'produk_jadi'; }

interface ConversionRow {
  source_inventory_id: number;
  source_qty: number;
  result_inventory_id: number;
  result_qty: number;
}

interface Production {
  id: number;
  date: string;
  location_name: string;
  username: string;
  notes: string;
  item_count: number;
}

interface ProductionDetail {
  production: {
    id: number; date: string; notes: string;
    location_name: string; username: string;
  };
  items: Array<{
    id: number;
    source_inventory_id: number; source_name: string; source_qty: number;
    result_inventory_id: number; result_name: string; result_qty: number;
  }>;
}

const today = () => new Date().toISOString().slice(0, 10);
const emptyRow = (): ConversionRow => ({ source_inventory_id: 0, source_qty: 1, result_inventory_id: 0, result_qty: 1 });

export default function Produksi() {
  const { role } = useAuth();
  const isAdmin = role === 'admin';

  const [productions, setProductions] = useState<Production[]>([]);
  const [inventories, setInventories] = useState<Inventory[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [detail, setDetail] = useState<ProductionDetail | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [form, setForm] = useState({ date: today(), location_id: '', notes: '' });
  const [rows, setRows] = useState<ConversionRow[]>([emptyRow()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    setLoading(true);
    const [prodRes, invRes, locRes] = await Promise.allSettled([
      api.get('/productions/getAll'),
      api.get('/inventories/getAllInventories'),
      api.get('/locations/getAllLocations'),
    ]);
    if (prodRes.status === 'fulfilled') setProductions(prodRes.value.data);
    if (invRes.status === 'fulfilled') setInventories(invRes.value.data);
    if (locRes.status === 'fulfilled') setLocations(locRes.value.data);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const openAdd = () => {
    setForm({ date: today(), location_id: '', notes: '' });
    setRows([emptyRow()]);
    setError('');
    setShowModal(true);
  };

  const openDetail = async (id: number) => {
    try {
      const res = await api.get(`/productions/getById/${id}`);
      setDetail(res.data);
    } catch {
      setError('Gagal memuat detail');
    }
  };

  const updateRow = (index: number, field: keyof ConversionRow, value: number) => {
    setRows((prev) => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rows.some((r) => !r.source_inventory_id || !r.result_inventory_id)) {
      setError('Pilih bahan baku dan produk hasil untuk setiap baris'); return;
    }
    if (rows.some((r) => r.source_inventory_id === r.result_inventory_id)) {
      setError('Bahan baku dan produk hasil tidak boleh sama'); return;
    }
    setSaving(true); setError('');
    try {
      await api.post('/productions/create', {
        ...form,
        location_id: form.location_id ? Number(form.location_id) : null,
        items: rows,
      });
      setShowModal(false);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/productions/delete/${deleteId}`);
      setDeleteId(null);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Gagal menghapus');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Produksi & Repacking</h1>
          <p className="text-gray-500 text-sm mt-1">Konversi bahan baku menjadi produk siap jual</p>
        </div>
        <button onClick={openAdd}
          className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-indigo-700 transition">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Catat Produksi
        </button>
      </div>

      {/* Info banner */}
      <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 flex items-start gap-3">
        <svg className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm text-amber-700">
          Gunakan fitur ini setelah mencatat pembelian bahan baku. Contoh: beli <strong>1 kg Kacang Curah</strong> → repacking jadi <strong>12 bungkus Kacang 100gr</strong>. Stok akan otomatis teradjust.
        </p>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">{error}</div>}

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48 text-gray-400">
            <svg className="animate-spin w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Memuat...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">ID</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tanggal</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Lokasi</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Operator</th>
                  <th className="text-center px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Item</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Catatan</th>
                  <th className="text-center px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {productions.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-12 text-gray-400">Belum ada data produksi</td></tr>
                ) : productions.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4 text-gray-400 text-xs">#{p.id}</td>
                    <td className="px-5 py-4 text-gray-600">{new Date(p.date).toLocaleDateString('id-ID')}</td>
                    <td className="px-5 py-4 text-gray-600">{p.location_name || '-'}</td>
                    <td className="px-5 py-4 text-gray-600">{p.username}</td>
                    <td className="px-5 py-4 text-center">
                      <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                        {p.item_count} konversi
                      </span>
                    </td>
                    <td className="px-5 py-4 text-gray-500 text-xs max-w-xs truncate">{p.notes || '-'}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => openDetail(p.id)}
                          className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition" title="Detail">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        </button>
                        {isAdmin && (
                          <button onClick={() => setDeleteId(p.id)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition" title="Hapus">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Form */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Catat Produksi / Repacking</h2>
                <p className="text-xs text-gray-400 mt-0.5">Bahan baku → Produk siap jual</p>
              </div>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-5">
              {error && <p className="text-red-500 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Tanggal *</label>
                  <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Lokasi</label>
                  <select value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    <option value="">-- Pilih Lokasi --</option>
                    {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Catatan</label>
                  <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="cth: repacking kacang tanggal ini" />
                </div>
              </div>

              {/* Konversi rows */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Daftar Konversi</label>
                  <button type="button" onClick={() => setRows([...rows, emptyRow()])}
                    className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    Tambah Baris
                  </button>
                </div>

                {/* Header kolom */}
                <div className="grid grid-cols-[1fr_80px_32px_1fr_80px_32px] gap-2 mb-1 px-1">
                  <p className="text-xs text-gray-400 font-medium">Bahan Baku</p>
                  <p className="text-xs text-gray-400 font-medium text-center">Qty Pakai</p>
                  <div />
                  <p className="text-xs text-gray-400 font-medium">Produk Hasil</p>
                  <p className="text-xs text-gray-400 font-medium text-center">Qty Jadi</p>
                  <div />
                </div>

                <div className="space-y-2">
                  {rows.map((row, i) => {
                    const srcInv = inventories.find((inv) => inv.id === row.source_inventory_id);
                    return (
                      <div key={i} className="grid grid-cols-[1fr_80px_32px_1fr_80px_32px] gap-2 items-center bg-gray-50 rounded-xl p-2">
                        {/* Bahan baku */}
                        <select value={row.source_inventory_id}
                          onChange={(e) => updateRow(i, 'source_inventory_id', Number(e.target.value))}
                          className="border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white">
                          <option value={0}>-- Bahan Baku --</option>
                          {inventories.filter((inv) => inv.jenis === 'bahan_baku').map((inv) => (
                            <option key={inv.id} value={inv.id}>{inv.name} (stok: {inv.stock})</option>
                          ))}
                        </select>
                        <div className="relative">
                          <input type="number" min={1}
                            max={srcInv?.stock ?? undefined}
                            value={row.source_qty}
                            onChange={(e) => updateRow(i, 'source_qty', Number(e.target.value))}
                            className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-center" />
                        </div>

                        {/* Panah */}
                        <div className="flex items-center justify-center text-indigo-400">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </div>

                        {/* Produk hasil */}
                        <select value={row.result_inventory_id}
                          onChange={(e) => updateRow(i, 'result_inventory_id', Number(e.target.value))}
                          className="border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white">
                          <option value={0}>-- Produk Hasil --</option>
                          {inventories
                            .filter((inv) => inv.jenis === 'produk_jadi' && inv.id !== row.source_inventory_id)
                            .map((inv) => (
                              <option key={inv.id} value={inv.id}>{inv.name} (stok: {inv.stock})</option>
                            ))}
                        </select>
                        <input type="number" min={1} value={row.result_qty}
                          onChange={(e) => updateRow(i, 'result_qty', Number(e.target.value))}
                          className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-center" />

                        {/* Hapus baris */}
                        {rows.length > 1 ? (
                          <button type="button" onClick={() => setRows(rows.filter((_, idx) => idx !== i))}
                            className="flex items-center justify-center text-red-400 hover:text-red-600 w-8 h-8">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        ) : <div />}
                      </div>
                    );
                  })}
                </div>

                {/* Summary */}
                {rows.some((r) => r.source_inventory_id && r.result_inventory_id) && (
                  <div className="mt-3 p-3 bg-indigo-50 rounded-xl space-y-1">
                    <p className="text-xs font-semibold text-indigo-700 mb-2">Ringkasan Perubahan Stok:</p>
                    {rows.filter((r) => r.source_inventory_id && r.result_inventory_id).map((r, i) => {
                      const src = inventories.find((inv) => inv.id === r.source_inventory_id);
                      const res = inventories.find((inv) => inv.id === r.result_inventory_id);
                      if (!src || !res) return null;
                      return (
                        <div key={i} className="flex items-center gap-2 text-xs text-indigo-600">
                          <span className="bg-red-100 text-red-600 px-2 py-0.5 rounded-full">−{r.source_qty} {src.name}</span>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                          <span className="bg-green-100 text-green-600 px-2 py-0.5 rounded-full">+{r.result_qty} {res.name}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50 transition">Batal</button>
                <button type="submit" disabled={saving}
                  className="flex-1 bg-indigo-600 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 transition">
                  {saving ? 'Menyimpan...' : 'Simpan & Adjust Stok'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
              <h2 className="text-lg font-semibold text-gray-800">Detail Produksi #{detail.production.id}</h2>
              <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {[
                  { label: 'Tanggal', value: new Date(detail.production.date).toLocaleDateString('id-ID', { dateStyle: 'long' }) },
                  { label: 'Lokasi', value: detail.production.location_name || '-' },
                  { label: 'Operator', value: detail.production.username },
                  { label: 'Catatan', value: detail.production.notes || '-' },
                ].map((row) => (
                  <div key={row.label} className={row.label === 'Catatan' ? 'col-span-2' : ''}>
                    <p className="text-xs text-gray-400 mb-0.5">{row.label}</p>
                    <p className="font-medium text-gray-800">{row.value}</p>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Konversi yang Dilakukan</p>
                <div className="space-y-3">
                  {detail.items.map((item) => (
                    <div key={item.id} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">
                            −{item.source_qty}
                          </span>
                          <span className="text-sm font-medium text-gray-700">{item.source_name}</span>
                        </div>
                      </div>
                      <svg className="w-4 h-4 text-indigo-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                      <div className="flex-1 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <span className="text-sm font-medium text-gray-700">{item.result_name}</span>
                          <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-full font-medium">
                            +{item.result_qty}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Hapus Data Produksi?</h3>
            <p className="text-gray-500 text-sm mb-6">Stok bahan baku akan dikembalikan dan stok produk hasil akan dikurangi.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50 transition">Batal</button>
              <button onClick={handleDelete} className="flex-1 bg-red-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-red-600 transition">Hapus</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
